import {
  CLOSE_ICON,
  DRAG_GRIP_ICON,
  launcherIcon,
  SEND_ARROW_ICON,
  SPARKLE_ICON_LARGE,
  SPARKLE_ICON_SMALL,
} from './icons'
import type { BotWidgetConfig } from './types'

// Host-supplied text dropped into a quoted attribute — a stray " would otherwise end it early.
function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

// Ported 1:1 from createWidget()'s widgetHTML template.
export function buildWidgetHTML(config: BotWidgetConfig): string {
  // Both this card and the greeting bubble below are closed-state teasers that exist
  // to draw attention to the launcher pill — with isShowLauncherIcon off there's no
  // pill for either to point at, so neither should render at all (matches
  // .bot-launcher-group's own gating just below).
  const showAiAssistantCard =
    config.isShowLauncherIcon && config.aiAssistant.isShow && !config.isSpanControl && !config.isHomeSite
  const assistantIcon = launcherIcon(config, false)
  const product = config.product?.toLowerCase().replace(/\s+/g, '') ?? ''
  const launcherLabel =
    config.launcherLabel ||
    (product.includes('taxbandits') ? 'Ask BanditAI' : product.includes('wealthrabbit') ? 'Ask RabbitAI' : 'Ask AI')
  const { greeting } = config
  const greetingQuestions = greeting.isShowQuestions === false ? [] : greeting.questions.slice(0, 4)

  return `
  <div class="bot-widget-container" style="color: #000;">
    ${
      config.isShowLauncherIcon && greeting.enabled
        ? `
      <div class='bot-greeting-widget'>
        <div class="bot-greeting">
          <button class="bot-greeting-close" aria-label="Close greeting">${CLOSE_ICON}</button>
          <div class="bot-greeting-header">
            <div class="bot-greeting-copy">
              ${greeting.title ? `<h2 class="bot-greeting-title">${greeting.title}</h2>` : ''}
              ${greeting.subtitle ? `<p class="bot-greeting-text">${greeting.subtitle}</p>` : ''}
            </div>
          </div>
          ${
            greetingQuestions.length > 0
              ? `
          <div class="bot-greeting-actions">
            ${
              greetingQuestions.length > 0
                ? `<p class="bot-greeting-label">${greeting.questionsLabel ?? 'Popular questions'}</p>`
                : ''
            }
            ${greetingQuestions
              .map(
                (question) => `
            <button class="bot-quick-question" data-question="${escapeAttr(question)}">${question}</button>
            `
              )
              .join('')}
          </div>
            `
              : ''
          }
          ${
            greeting.isEnableAskInput !== false
              ? `
          <div class="bot-greeting-footer">
            <form class="bot-greeting-ask">
              <input
                class="bot-greeting-ask-input"
                type="text"
                placeholder="${escapeAttr(greeting.askInputPlaceholder ?? 'Or type your question')}"
                aria-label="Type your question"
                autocomplete="off"
              />
              <button class="bot-greeting-ask-send" type="submit" aria-label="Send question" disabled>${SEND_ARROW_ICON}</button>
            </form>
          </div>
          `
              : ''
          }
        </div>
      </div>
      `
        : ''
    }
     <!-- AI Assistant Card -->
     ${
       showAiAssistantCard
         ? `
       <div class="ai-assistant-card">
        <button class="ai-close-icon" aria-label="Close assistant">×</button>
        <div class="ai-assistant-content">
        ${assistantIcon ? assistantIcon : `<div class="ai-icon">${config.aiAssistant.icon || 'AI'}</div>`}
          <h2 class="widget-title">${config.aiAssistant.title || 'AI Assistant'}</h2>
          <h3 class="widget-subtitle">${config.aiAssistant.subtitle || 'Your Instant Help, Anytime!'}</h3>
          <p class="widget-description">
            ${config.aiAssistant.description || 'Get instant answers, insights, and guidance directly inside the application.'}
          </p>
          <button class="ai-try-now-btn">Try it now!</button>
        </div>
      </div>
      `
         : ''
     }

   ${
     config.isShowLauncherIcon
       ? `
    <div class="bot-launcher-group${config.isEnableLauncherDrag ? ' draggable' : ''}">
      ${config.isEnableLauncherDrag ? `<span class="drag-indicator" aria-hidden="true">${DRAG_GRIP_ICON}</span>` : ''}
      <button type="button" class="bot-launcher-btn bot-launcher-primary${config.launcherClass ? ` ${escapeAttr(config.launcherClass)}` : ''}" aria-label="Open chat">
        <span class="bot-badge">0</span>
        <span class="bot-launcher-icon">${SPARKLE_ICON_LARGE}${SPARKLE_ICON_SMALL}</span>
        <span class="bot-launcher-label">${launcherLabel}</span>
      </button>
    </div>`
       : ''
   }

    <div class="bot-chat-window">
      <iframe class="bot-chat-iframe" id="bot-chat-iframe"></iframe>
    </div>
    <button class="bot-floating-close-btn" aria-label="Close chat" type="button">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
      Close
    </button>
    <div class="bot-error-message"></div>
    <div class="application-error-ans">
      <button class="close-icon" aria-label="Close greeting">X</button>
      <div class="error-ans"></div>
    </div>
</div>
  </div>
`
}

// Ported 1:1 from createQuickHelpWidget()'s innerHTML template. The favicon
// path is derived from the *host page's* origin, not the bot's — matches
// the original, which never referenced config.botUrl here either.
export function buildQuickHelpHTML(): string {
  const imagePath = location.origin.includes('localhost')
    ? 'http://localhost:5173'
    : `https://chatui.${location.origin.split('.')[1]}.com`
  return `
    <div class="quick-help-bot-chat-window">
      <div class="quick-help-bot-chat-header">
        <div class="quick-help-header-info">
          <div class="quick-help-avatar">
            <img
              alt="Bandit AI"
              width="19"
              style="max-width: 30px;"
              src="${imagePath}/favicon.ico"
            />
          </div>
          <h3 class="quick-help-title">Tax Copilot</h3>
        </div>
        <button class="quick-help-close">✕</button>
      </div>
        <iframe class="quick-help-bot-chat-iframe" id="quick-help-bot-chat-iframe"></iframe>
    </div>
  `
}
